import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Calendar, MapPin, Info } from 'lucide-react';
import { getEvents } from '../services/api';
import { EventDetailsModal } from './EventDetailsModal';
import { FloatingDots } from './FloatingDots';
import { useIsMobile } from '../hooks/useIsMobile';

export const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleElements, setVisibleElements] = useState([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    // Intersection Observer for scroll-based animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => [...new Set([...prev, entry.target.dataset.index])]);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [events]);

  const fetchEvents = async () => {
    try {
      const response = await getEvents();
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="events" className="py-16 md:py-24 bg-gradient-to-b from-black via-green-950/10 to-black relative overflow-hidden">
        <FloatingDots count={isMobile ? 80 : 150} primaryColor="green" secondaryColor="orange" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <p className="text-white text-xl">Loading events...</p>
        </div>
      </section>
    );
  }
  
  return (
    <section id="events" className="py-16 md:py-24 bg-gradient-to-b from-black via-green-950/10 to-black relative overflow-hidden">
      <FloatingDots count={isMobile ? 80 : 150} primaryColor="green" secondaryColor="orange" />
      <div className="container mx-auto px-4 relative z-10">
          <div 
            className="text-center mb-12 md:mb-16 transition-all duration-700 ease-out"
            data-animate
            data-index="header"
            style={{
              opacity: visibleElements.includes('header') ? 1 : 0,
              transform: visibleElements.includes('header') ? 'translateY(0)' : 'translateY(30px)'
            }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-orange-400 hover:scale-105 transition-transform duration-300 cursor-default inline-block leading-tight pb-2">
              Upcoming Events
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-green-500 to-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-400 text-base md:text-lg hover:text-gray-300 transition-colors duration-300">Join us on the dancefloor</p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
            {events.map((event, index) => (
              <Card 
                key={event.id}
                className="bg-black/50 border-green-500/30 p-8 hover:border-green-500 transition-all duration-300 group cursor-pointer"
                onClick={() => {
                  setSelectedEvent(event);
                  setIsModalOpen(true);
                }}
                data-animate
                data-index={`event-${index}`}
                style={{
                  opacity: visibleElements.includes(`event-${index}`) ? 1 : 0,
                  transform: visibleElements.includes(`event-${index}`) ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ease-out ${index * 0.1}s`
                }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-green-400 transition-colors duration-300">
                      {event.name}
                    </h3>
                    
                    <div className="space-y-2 text-gray-400">
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 mr-3 text-orange-400" />
                        <span>{event.date}</span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="w-5 h-5 mr-3 text-orange-400" />
                        <span>{event.venue}, {event.city}</span>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    className="bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600 text-white shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transition-all duration-300 group-hover:scale-105"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEvent(event);
                      setIsModalOpen(true);
                    }}
                  >
                    <Info className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </div>
              </Card>
            ))}
          </div>
          
          {/* Event Details Modal */}
          <EventDetailsModal 
            event={selectedEvent}
            open={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedEvent(null);
            }}
          />
      </div>
    </section>
  );
};
